// src/mocks/handlers/dashboard/topOutstandingInvoicesHandler.ts

import { rest } from 'msw';
import c1OutstandingResults from '@/mocks/data/dashboard_c1_outstanding_topOutstandingInvoices.json';
import c1ExpectedResults from '@/mocks/data/dashboard_c1_expected_topOutstandingInvoices.json';
import c2OutstandingResults from '@/mocks/data/dashboard_c2_outstanding_topOutstandingInvoices.json';
import c2ExpectedResults from '@/mocks/data/dashboard_c2_expected_topOutstandingInvoices.json';
import c3OutstandingResults from '@/mocks/data/dashboard_c3_outstanding_topOutstandingInvoices.json';
import c3ExpectedResults from '@/mocks/data/dashboard_c3_expected_topOutstandingInvoices.json';
import c4OutstandingResults from '@/mocks/data/dashboard_c4_outstanding_topOutstandingInvoices.json';
import c4ExpectedResults from '@/mocks/data/dashboard_c4_expected_topOutstandingInvoices.json';
import c5OutstandingResults from '@/mocks/data/dashboard_c5_outstanding_topOutstandingInvoices.json';
import c5ExpectedResults from '@/mocks/data/dashboard_c5_expected_topOutstandingInvoices.json';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import { ITopOutstandingInvoicesResponse } from '@/domains/dashboard/topOutstandingInvoices/topOutstandingInvoices.types.ts';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';

const MOCK_RESPONSES: Record<
  string,
  {
    outstanding:
      | typeof c1OutstandingResults
      | typeof c2OutstandingResults
      | typeof c3OutstandingResults
      | typeof c4OutstandingResults
      | typeof c5OutstandingResults;
    expected_repayment_date:
      | typeof c1ExpectedResults
      | typeof c2ExpectedResults
      | typeof c3ExpectedResults
      | typeof c4ExpectedResults
      | typeof c5ExpectedResults;
  }
> = {
  c1: {
    outstanding: c1OutstandingResults,
    expected_repayment_date: c1ExpectedResults,
  },
  c2: {
    outstanding: c2OutstandingResults,
    expected_repayment_date: c2ExpectedResults,
  },
  c3: {
    outstanding: c3OutstandingResults,
    expected_repayment_date: c3ExpectedResults,
  },
  c4: {
    outstanding: c4OutstandingResults,
    expected_repayment_date: c4ExpectedResults,
  },
  c5: {
    outstanding: c5OutstandingResults,
    expected_repayment_date: c5ExpectedResults,
  },
};

export const topOutstandingInvoicesHandler =
  rest.get<ITopOutstandingInvoicesResponse>(
    `${VITE_MSW_BASE_URL}${API_ENDPOINTS.DASHBOARD_TOP_OUTSTANDING_INVOICES}`,
    (req, res, ctx) => {
      const token = req.headers.get('Authorization');
      if (!token) {
        return res(
          ctx.status(500),
          ctx.json({ detail: 'Something went wrong' }),
        );
      }

      if (isAccessTokenExpired()) {
        return res(
          ctx.status(401),
          ctx.json({ error: 'Access token expired' }),
        );
      }

      const { customer_uuid } = req.params as { customer_uuid: string };
      const ordering = req.url.searchParams.get('ordering');

      const customerData = MOCK_RESPONSES[customer_uuid];

      if (!customerData) {
        return res(ctx.status(404), ctx.json({ error: 'Customer not found' }));
      }

      const outstandingResults = customerData.outstanding;
      const expectedResults = customerData.expected_repayment_date;

      let mockResponse;
      if (ordering === 'outstanding') {
        const sorted = [...outstandingResults.results].sort(
          (a, b) => (b.outstanding ?? 0) - (a.outstanding ?? 0),
        );
        mockResponse = { results: sorted };
      } else {
        mockResponse = expectedResults;
      }

      // Creator dashboard requires campaign columns in top outstanding invoices.
      if (customer_uuid === 'c5') {
        mockResponse = {
          results: mockResponse.results.map((item, index) => ({
            ...item,
            campaign_name: `Creator Campaign ${index + 1}`,
            campaign_id: `CR-${String(index + 1).padStart(3, '0')}`,
          })),
        };
      }

      return res(ctx.status(200), ctx.json(mockResponse));
    },
  );
